import {
	ReactElement,
	MouseEvent,
	useCallback
} from "react";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { FileThumbnailKind, getDocumentKind } from "client_legacy/Documents/fileExtensions";
import {
	DateString,
	Icon,
	Space,
	Highlight,
	HighlightedText,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { useHighlights } from "../../Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { EgnyteGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { formatBreadCrumb } from "../../Layout/TitleBar/GlobalSearch/Items/EgnyteListItem";
import { ItemLayout } from "./ItemLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { useEgnyteNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useEgnyteNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type EgnyteItemViewProps = {
	item: EgnyteGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	timezone: TimeZone;
};

export const EgnyteItemView = (props: EgnyteItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		timezone
	} = props;

	const { url } = item;

	const executeHandler: () => void = useEgnyteNavigationHandler({
		url,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("egnyte-item", `|${url}`)}
		>
			<EgnyteItem
				item={item}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
EgnyteItemView.displayName = "EgnyteItemView";

type EgnyteItemProps = {
	item: EgnyteGlobalSearchResultItem;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const EgnyteItem = (
	props: EgnyteItemProps
): ReactElement => {
	const {
		item,
		timezone,
		navigationIndex,
		onExecute
 } = props;

	const {
		name,
		extension,
		snippet,
		path,
		lastModifiedDate,
		locationUrl,
		modifiedBy,
		hitHighlights
	} = item;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const type: FileThumbnailKind = getDocumentKind(
		DocumentItemType.File,
		extension
	);

	const locationClickHandler = useCallback(
		(event: MouseEvent): void => {
			event.stopPropagation();
			navigateTo(locationUrl, true);
		},
		[locationUrl]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="egnyte-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<Icon
					value={{ kind: "svg", name: type }}
				/>
			</ItemLayout.IconContainer>

			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="title"
					title={name}
				>
					<HighlightedText
						text={name}
						highlight={highlights.get("name")}
					/>
				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.InfoIconWrapper>
						<Icon value="pen" size="small" />
					</ItemLayout.InfoIconWrapper>

					<ItemLayout.SubTitle>
						<span {...setTrackingId("modified-date")}>
							<DateString
								format="RelativeTimestamp"
								value={lastModifiedDate}
								timezone={timezone}
							/>
						</span>
						<Space />
						{localize("_(Edited)By")}
						<Space />
						<span {...setTrackingId("modified-by")}>
							{modifiedBy}
						</span>
						<PipeSeparator />
						<ItemLayout.Link
							onClick={locationClickHandler}
							trackingId="location"
						>
							{formatBreadCrumb(path)}
						</ItemLayout.Link>
					</ItemLayout.SubTitle>
				</ItemLayout.Info>

				{snippet && (
					<ItemLayout.Content>
						<HighlightedText
							text={snippet}
							highlight={highlights.get("snippet")}
						/>
					</ItemLayout.Content>
				)}
			</ItemLayout.InfoWrapper>
			<ItemLayout.LocationButton
				onClick={locationClickHandler}
			/>
		</ItemLayout>
	);
};

EgnyteItem.displayName = "EgnyteItem";
